import React, { useEffect, useRef, useState } from "react";
import { useLocale } from "../../i18n/LocaleContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATS = [
  { value: 30, suffix: "M+", label: "Indirect SKUs unified in one digital catalog" },
  { value: 1.26, suffix: "T", prefix: "$", decimals: 2, label: "Fortune 2000 addressable indirect spend, annually" },
  { value: 23, suffix: "k", label: "Tail suppliers consolidated per enterprise, on average" },
  { value: 80, suffix: "+", label: "Countries of operational coverage on day one" },
];

function useCountUp(target, run, decimals = 0) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!run) return;
    let raf;
    const start = performance.now();
    const duration = 1400;
    const animate = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const next = eased * target;
      setValue(decimals ? parseFloat(next.toFixed(decimals)) : Math.floor(next));
      if (t < 1) raf = requestAnimationFrame(animate);
      else setValue(target);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [target, run, decimals]);
  return value;
}

function Stat({ value, prefix = "", suffix, label, run, decimals = 0 }) {
  const v = useCountUp(value, run, decimals);
  const formatted = decimals
    ? v.toFixed(decimals)
    : value >= 1000
      ? v.toLocaleString()
      : v;
  return (
    <div
      data-testid={`scale-stat-${label.slice(0, 10).toLowerCase().replace(/\s+/g, "-")}`}
      className="rounded-xl border border-white/10 bg-white/[0.03] p-6 md:p-8"
    >
      <div className="font-display text-4xl md:text-5xl font-bold tracking-tight text-white">
        {prefix}
        {formatted}
        <span className="text-[#7DD3FC]">{suffix}</span>
      </div>
      <div className="mt-2 text-slate-300/85 text-sm md:text-base leading-snug">
        {label}
      </div>
    </div>
  );
}

export default function Scale() {
  const { t } = useLocale();
  const ref = useRef(null);
  const [run, setRun] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setRun(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.25 }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      data-testid="scale-section"
      className="relative bg-slate-950 text-white py-24 md:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 -z-10">
        <img
          src={`${BACKEND_URL}/api/static/site/warehouse-hero.png`}
          alt="Modern enterprise distribution operations"
          className="w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-950/90 to-slate-950" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-12">
          <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.22em] text-slate-400 font-semibold mb-4">
            <span className="inline-block h-px w-8 bg-white/20" />
            <span>{t("landing.scale.eyebrow")}</span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl xl:text-[56px] font-semibold tracking-[-0.02em] mt-1 leading-[1.05]">
            {t("landing.scale.headline_a")} <span className="text-slate-400">{t("landing.scale.headline_b")}</span>
          </h2>
          <p className="mt-4 text-slate-300/90 text-lg leading-relaxed font-light">
            {t("landing.scale.body")}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {STATS.map((s) => (
            <Stat key={s.label} {...s} run={run} />
          ))}
        </div>
      </div>
    </section>
  );
}
